import {
  createRootRouteWithContext,
  lazyRouteComponent,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import type { PlatformRole, Surface } from '@lb/domain'
import { LOCALE_PREFIX, basepathFor, splitLocalePath } from '@lb/i18n'

import { AppShell } from '../shell/app-shell'
import { useSessionContext } from '../providers/session'
import { useStorm } from '../providers/storm'
import { HomeScreen } from '../../routes/home/home'
import { CommunityRoomNav } from '../../routes/community/room-nav'
import { resourcesSearchSchema } from '../../routes/resources/search-params'
import { searchSearchSchema } from '../../routes/search/search-params'
import { loginSearchSchema, nextFrom, isSafeNextPath } from './next-path'
import {
  confirmEmailSearchSchema,
  resetPasswordSearchSchema,
  seatInvitationSearchSchema,
} from '../../public/account/search-params'
import { OrganizationSchema, Seo } from '../shell/seo'
import { signupSearchSchema } from '../../public/onboarding/signup-intent'

/**
 * The route tree, code-based rather than file-based.
 *
 * Code-based because the tree is small enough to read in one screen and because
 * it avoids a second codegen step in the critical path — `openapi-typescript`
 * is enough. If the tree grows past what fits here, `@tanstack/router-plugin`
 * is a drop-in.
 *
 * Routes are REAL and linkable. The prototypes switch views in JS; the design
 * spec is explicit that production uses routes so pages are shareable and
 * back/forward works.
 *
 * Written out one by one rather than through a helper: TanStack Router derives
 * the literal path types from these calls, and a helper taking `path: string`
 * erases exactly the type safety that made it worth choosing.
 */

/**
 * Every screen except Home is a lazy chunk.
 *
 * ── What this fixes ───────────────────────────────────────────────────────
 * The whole product shipped as one 713 kB script (210 kB gzipped). A parent
 * opening Home downloaded the cohort journey, the seat administration table,
 * the checkout funnel and the audio player before anything rendered — most of
 * which their role cannot even reach.
 *
 * Home stays eager because it IS the first paint for a signed-in member;
 * making it lazy would only add a round trip. The funnel and login are lazy for
 * the mirror-image reason: a signed-in member never needs them.
 *
 * `lazyRouteComponent` rather than bare `React.lazy` — the router preloads on
 * intent (`defaultPreload: 'intent'`), so a chunk is usually already in flight
 * by the time a link is clicked, and there is no Suspense fallback to design.
 */
const AnnouncementsScreen = lazyRouteComponent(() => import('../../routes/announcements/announcements'), 'AnnouncementsScreen')
const AudioScreen = lazyRouteComponent(() => import('../../routes/resources/audio'), 'AudioScreen')
const BillingScreen = lazyRouteComponent(() => import('../../routes/billing/billing'), 'BillingScreen')
const ChangeEmailScreen = lazyRouteComponent(() => import('../../routes/billing/change-email'), 'ChangeEmailScreen')
const ChangePasswordScreen = lazyRouteComponent(() => import('../../routes/billing/change-password'), 'ChangePasswordScreen')
const DeleteAccountScreen = lazyRouteComponent(() => import('../../routes/billing/delete-account'), 'DeleteAccountScreen')
const ChangePlanScreen = lazyRouteComponent(() => import('../../routes/billing/change-plan'), 'ChangePlanScreen')
const ManageSubscriptionScreen = lazyRouteComponent(() => import('../../routes/billing/manage'), 'ManageSubscriptionScreen')
const PaymentMethodScreen = lazyRouteComponent(() => import('../../routes/billing/payment'), 'PaymentMethodScreen')
const InvoicesScreen = lazyRouteComponent(() => import('../../routes/billing/invoices'), 'InvoicesScreen')
const InvoiceScreen = lazyRouteComponent(() => import('../../routes/billing/invoice'), 'InvoiceScreen')
const ConfirmEmailScreen = lazyRouteComponent(() => import('../../public/account/confirm-email'), 'ConfirmEmailScreen')
const SessionHandoffScreen = lazyRouteComponent(() => import('../../public/account/session-handoff'), 'SessionHandoffScreen')
const ConfirmPasswordResetScreen = lazyRouteComponent(() => import('../../public/account/password-reset'), 'ConfirmPasswordResetScreen')
const RequestPasswordResetScreen = lazyRouteComponent(() => import('../../public/account/password-reset'), 'RequestPasswordResetScreen')
const AcceptSeatInvitationScreen = lazyRouteComponent(() => import('../../public/account/password-reset'), 'AcceptSeatInvitationScreen')
const BookmarksScreen = lazyRouteComponent(() => import('../../routes/community/bookmarks'), 'BookmarksScreen')
const CaseStudyScreen = lazyRouteComponent(() => import('../../routes/cases/cases'), 'CaseStudyScreen')
const CasesScreen = lazyRouteComponent(() => import('../../routes/cases/cases'), 'CasesScreen')
const ChallengeScreen = lazyRouteComponent(() => import('../../routes/challenge/challenge'), 'ChallengeScreen')
const CohortScreen = lazyRouteComponent(() => import('../../routes/cohort/cohort'), 'CohortScreen')
const CollectionScreen = lazyRouteComponent(() => import('../../routes/resources/collection'), 'CollectionScreen')
const CommunityScreen = lazyRouteComponent(() => import('../../routes/community/community'), 'CommunityScreen')
const CourseScreen = lazyRouteComponent(() => import('../../routes/resources/course'), 'CourseScreen')
const EventsScreen = lazyRouteComponent(() => import('../../routes/events/events'), 'EventsScreen')
const JourneyScreen = lazyRouteComponent(() => import('../../routes/journey/journey'), 'JourneyScreen')
const PrivacyScreen = lazyRouteComponent(
  () => import('../../public/legal/legal'),
  'PrivacyScreen',
)
const TermsScreen = lazyRouteComponent(() => import('../../public/legal/legal'), 'TermsScreen')
const SupportScreen = lazyRouteComponent(() => import('../../public/legal/legal'), 'SupportScreen')
const LoginScreen = lazyRouteComponent(() => import('../../public/login/login'), 'LoginScreen')
const OnboardingFunnel = lazyRouteComponent(() => import('../../public/onboarding/funnel'), 'OnboardingFunnel')
const PodcastScreen = lazyRouteComponent(() => import('../../routes/podcasts/podcasts'), 'PodcastScreen')
const PodcastsScreen = lazyRouteComponent(() => import('../../routes/podcasts/podcasts'), 'PodcastsScreen')
const PrintableScreen = lazyRouteComponent(() => import('../../routes/resources/printable'), 'PrintableScreen')
const ProfileScreen = lazyRouteComponent(() => import('../../routes/profile/profile'), 'ProfileScreen')
const BlockedMembersScreen = lazyRouteComponent(() => import('../../routes/profile/blocked-members'), 'BlockedMembersScreen')
const PractitionerScreen = lazyRouteComponent(() => import('../../routes/practitioners/practitioner'), 'PractitionerScreen')
const QandaItemScreen = lazyRouteComponent(() => import('../../routes/qanda/qanda'), 'QandaItemScreen')
const QandaScreen = lazyRouteComponent(() => import('../../routes/qanda/qanda'), 'QandaScreen')
const ReadingScreen = lazyRouteComponent(() => import('../../routes/resources/reading'), 'ReadingScreen')
const ResourcesScreen = lazyRouteComponent(() => import('../../routes/resources/resources'), 'ResourcesScreen')
const RoomScreen = lazyRouteComponent(() => import('../../routes/community/community'), 'RoomScreen')
const ThreadScreen = lazyRouteComponent(() => import('../../routes/community/thread'), 'ThreadScreen')
const SearchScreen = lazyRouteComponent(() => import('../../routes/search/search'), 'SearchScreen')
const SeatsScreen = lazyRouteComponent(() => import('../../routes/seats/seats'), 'SeatsScreen')

export interface RouterContext {
  role: PlatformRole | undefined
  canSee: (role: PlatformRole, surface: Surface) => boolean
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout })

/**
 * Routes that render standalone — no authenticated app shell around them —
 * *regardless of session state*.
 *
 * The funnel has its own `FunnelShell` (its own topbar, logo, Log-in link, and
 * progress indicator). If a signed-in member navigates back to `/signup` or
 * `/login`, wrapping their funnel in the app shell composes the two: a nav
 * sidebar, search box, notifications badge and profile chip appear around a
 * flow whose whole premise is that the visitor is unauthenticated. That is
 * exactly the "how is this state even possible" bug — a member logged in as
 * Camille purchasing an account.
 *
 * Kept as prefixes: `/signup`, `/login`, `/account/…` (password reset / email
 * confirm), `/legal/…` (privacy, terms), `/support`. Locale-prefixed variants (`/en/…`,
 * `/fr/…`) match too, because the locale wrapper is transparent.
 *
 * Home (`/`) is NOT here: it renders the landing page for a visitor and Home
 * for a member, and Home *needs* the shell.
 */
const STANDALONE_PATHS = ['/signup', '/login', '/account', '/legal', '/support']

function isStandalonePath(pathname: string): boolean {
  const { path: unprefixed } = splitLocalePath(pathname)
  return STANDALONE_PATHS.some(
    (prefix) => unprefixed === prefix || unprefixed.startsWith(`${prefix}/`),
  )
}

function RootLayout() {
  const { viewer, isError } = useSessionContext()
  const navigate = useNavigate()
  const storm = useStorm()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const searchStr = useRouterState({ select: (state) => state.location.searchStr })

  /*
   * Unauthenticated.
   *
   * The public routes render without the shell, as they always have. A PRIVATE
   * one now redirects to sign-in instead of rendering itself: it used to mount
   * anyway, fire every request at a server that answered 401, and settle on
   * « Les publications ne se chargent pas » over an empty page — which reads as
   * a broken product to somebody who simply was not signed in. Shared links to
   * a room, a course or an event are the common way in, so this is the path
   * most likely to be somebody's first impression.
   *
   * `isError` counts as signed out here, and deliberately: the router only
   * mounts once the session resolves (`app.tsx`), and for a visitor with no
   * session that resolution IS the error. The two are not separable at this
   * seam, and sign-in is the right destination for both — a member whose API is
   * unreachable meets a form that tells them so, rather than a dead page.
   */
  if (isError || !viewer) {
    if (isStandalonePath(pathname) || pathname === '/') return <Outlet />

    const next = nextFrom(pathname, searchStr)
    return <Navigate to="/login" search={next ? { next } : {}} replace />
  }

  // Signed-in visitors navigating back into the funnel/public routes render
  // standalone too — the funnel has its own shell and composing the two shows
  // the sidebar around an unauthenticated flow.
  if (isStandalonePath(pathname)) return <Outlet />

  return (
    <AppShell
      role={viewer.session.platformRole}
      pathname={pathname}
      onNavigate={(to) => void navigate({ to })}
      onOpenStorm={storm.open}
      /*
       * The rooms nest under Community in the design's rail, and only there.
       * Passed from here rather than read inside the shell because the shell
       * has no business knowing what a room is — `aside` is the slot it already
       * offered and nothing had ever filled.
       */
      aside={
        pathname.startsWith('/community') ? (
          <CommunityRoomNav activeRoomId={pathname.split('/community/')[1]} />
        ) : null
      }
    >
      <Outlet />
    </AppShell>
  )
}

/**
 * A surface guard.
 *
 * Reflects permissions, does not enforce them: a Parent reaching `/cases` is
 * redirected, and if they somehow reached the fetch the server answers 404
 * anyway — deliberately, since revealing that Pro-only content exists at an id
 * is itself the leak. This exists so nobody is shown a door they cannot open.
 *
 * The router does not mount until the session has resolved (see `app.tsx`), so
 * `context.role` is defined here for every authenticated caller. An undefined
 * role means genuinely unauthenticated and belongs to the funnel, not to a
 * redirect from here.
 */
function requireSurface(surface: Surface) {
  return ({ context }: { context: RouterContext }) => {
    if (context.role && !context.canSee(context.role, surface)) {
      /*
       * TanStack Router's redirect() returns a control-flow signal the router
       * catches by shape, not an Error. Wrapping it in one stops the redirect
       * working, so the rule is right in general and wrong here.
       */
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/' })
    }
  }
}

/**
 * `/` is Home for a member and the landing page for everyone else.
 *
 * One route rather than two, because it is one URL: a signed-out visitor and a
 * signed-in member type the same thing and expect different pages. The session
 * has already resolved by the time this renders (see `app.tsx`), so there is no
 * flash of the wrong one.
 */
function IndexScreen() {
  const { viewer } = useSessionContext()
  const navigate = useNavigate()

  if (!viewer) {
    return (
      <>
        {/* The only page that should ever be indexed. */}
        <Seo
          titleKey="seo.landingTitle"
          descriptionKey="seo.landingDescription"
          path="/"
          indexable
        />
        <OrganizationSchema />
        <OnboardingFunnel
          onEnterApp={() => window.location.assign(homeInCurrentLocale())}
          onSignIn={() => void navigate({ to: '/login' })}
        />
      </>
    )
  }

  // No <Seo> here: `AppShell` asserts `noindex` for every authenticated route
  // structurally, and `RouteAnnouncer` owns the title.
  return <HomeScreen />
}

const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: IndexScreen })

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupScreen,
  validateSearch: signupSearchSchema,
})

function SignupScreen() {
  const navigate = useNavigate()
  const intent = signupRoute.useSearch()
  return (
    <>
      <Seo
        titleKey="seo.signupTitle"
        descriptionKey="seo.signupDescription"
        path="/signup"
        indexable
      />
      <OnboardingFunnel
      // A full reload rather than a client navigation: the session must be
      // re-read from scratch once an account exists, and the router was built
      // with an unauthenticated context.
      onEnterApp={() => window.location.assign(homeInCurrentLocale())}
        onSignIn={() => void navigate({ to: '/login' })}
        intent={intent}
        onIntentApplied={() => void navigate({ to: '/signup', search: {}, replace: true })}
      />
    </>
  )
}

/**
 * The home URL under the caller's current locale. Full-page reload is
 * required after a signup or sign-in: the router mounted with an
 * unauthenticated context and the session must be re-read from scratch. That
 * is why `window.location.assign` and not `navigate`.
 *
 * Without this, the Stripe post-payment "Enter" button navigated to `/`
 * regardless of the visitor's language — an English signup landed on the
 * French home. Same for the post-login redirect and the two "onEnterApp"
 * call sites above.
 */
function homeInCurrentLocale(): string {
  const { locale } = splitLocalePath(window.location.pathname)
  return `${LOCALE_PREFIX[locale]}/`
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginRouteScreen,
  validateSearch: loginSearchSchema,
})

function LoginRouteScreen() {
  const navigate = useNavigate()
  const { next } = loginRoute.useSearch()

  /*
   * Back where they were going, or home. Checked again here rather than trusted
   * from the parse: this is the line that actually hands the browser over, and
   * it should be safe to read on its own.
   *
   * A full assign rather than a client navigation, which is what this screen
   * already did: the session, the query cache and the locale basepath are all
   * established at boot, and a member arriving with a brand-new session should
   * get a clean one rather than the signed-out tree patched in place.
   */
  const afterSignIn = () => {
    const { locale } = splitLocalePath(window.location.pathname)
    const target = next && isSafeNextPath(next) ? `${LOCALE_PREFIX[locale]}${next}` : null
    window.location.assign(target ?? homeInCurrentLocale())
  }

  return (
    <>
      <Seo
        titleKey="seo.loginTitle"
        descriptionKey="seo.loginDescription"
        path="/login"
        indexable
      />
      <LoginScreen
        onSignedIn={afterSignIn}
        onSignUp={() => void navigate({ to: '/signup' })}
        onResumeSignup={() => void navigate({ to: '/signup' })}
      />
    </>
  )
}

/*
 * Public and indexable: a privacy policy nobody can find is not published, and
 * Law 25 requires publication. `robots.txt` allows `/legal/` for the same
 * reason.
 */
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/privacy',
  component: PrivacyScreen,
})

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/terms',
  component: TermsScreen,
})

/* The App Store's support URL — public and indexable for the same reason. */
const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/support',
  component: SupportScreen,
})

const journeyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/journey',
  component: JourneyScreen,
  // TSP holds one journey — the cohort's. The voyage comes with the Pro role.
  beforeLoad: requireSurface('journey'),
})

const cohortRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cohort',
  component: CohortScreen,
  beforeLoad: requireSurface('cohort'),
})

const resourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources',
  component: ResourcesScreen,
  /*
   * Filters live in the URL, validated here. `.catch()` on every member means a
   * truncated or hand-edited link falls back to unfiltered rather than throwing
   * a route error at somebody who pasted it from a message.
   */
  validateSearch: resourcesSearchSchema,
})

const courseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources/courses/$id',
  component: CourseScreen,
})

/*
 * Same component, deeper URL — a member on Module 2 has a shareable
 * link the outline can navigate to and the back button can step
 * through. `$ordinal` is 1-indexed and matches the outline label
 * ("Module 2") for readability; an out-of-range ordinal degrades to
 * Module 1 rather than a 404, because a shared link that lost a
 * module after an edit should still land on the course.
 */
const courseModuleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources/courses/$id/modules/$ordinal',
  component: CourseScreen,
})

const collectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources/collections/$id',
  component: CollectionScreen,
})

const audioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources/audios/$id',
  component: AudioScreen,
})

const printableRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources/printables/$id',
  component: PrintableScreen,
})

const readingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources/readings/$id',
  component: ReadingScreen,
})

const podcastsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/podcasts',
  component: PodcastsScreen,
})

const podcastRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/podcasts/$id',
  component: PodcastScreen,
})

const qandaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/qanda',
  component: QandaScreen,
  beforeLoad: requireSurface('qanda'),
})

const qandaItemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/qanda/$id',
  component: QandaItemScreen,
  beforeLoad: requireSurface('qanda'),
})

const casesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cases',
  component: CasesScreen,
  beforeLoad: requireSurface('caseStudies'),
})

const caseStudyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cases/$id',
  component: CaseStudyScreen,
  beforeLoad: requireSurface('caseStudies'),
})

const eventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/events',
  component: EventsScreen,
})

const communityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community',
  component: CommunityScreen,
})

const roomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/$roomId',
  component: RoomScreen,
})

/*
 * One post, on its own page — the mock's ThreadView. A room's card list links
 * here, so a reply is written on a page whose URL names the conversation.
 */
const threadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/$roomId/posts/$postId',
  component: ThreadScreen,
})

const bookmarksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookmarks',
  component: BookmarksScreen,
})

const challengeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/challenge',
  component: ChallengeScreen,
  beforeLoad: requireSurface('challenge'),
})

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  component: SearchScreen,
  /*
   * The topbar search on every page lands here carrying `?q`. It has to be a
   * router navigation rather than a native GET, because a native GET resolves
   * against the origin and drops the locale basepath — searching from an /en
   * page landed on the French /search.
   */
  validateSearch: searchSearchSchema,
})

const announcementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/announcements',
  component: AnnouncementsScreen,
})

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: ProfileScreen,
})

/* « Membres bloqués », from Profile › Privacy (App Store guideline 1.2). */
const blockedMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile/blocked-members',
  component: BlockedMembersScreen,
})

const practitionerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/practitioners/$userId',
  component: PractitionerScreen,
})

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  component: BillingScreen,
})

/*
 * The two account sub-pages the design reaches from Profile › Account. Real
 * URLs under `/billing` because that is where the design's own crumb trail puts
 * them — "Plan & billing › Change password" — and because a password form
 * somebody was interrupted on should be somewhere they can get back to.
 */
const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/password',
  component: ChangePasswordScreen,
})

/* « Supprimer mon compte », from Profile › Confidentialité. Under `/billing`
   with the other account pages that ask for a password. */
const deleteAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/delete-account',
  component: DeleteAccountScreen,
})

const changeEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/email',
  component: ChangeEmailScreen,
})

const changePlanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/change-plan',
  component: ChangePlanScreen,
})

const manageSubscriptionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/manage',
  component: ManageSubscriptionScreen,
})

const paymentMethodRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/payment',
  component: PaymentMethodScreen,
})

const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/invoices',
  component: InvoicesScreen,
})

/*
 * The path the three renewal mails link to. It must stay exactly
 * `/billing/invoices/<id>` — `billingLink` on the backend mints that string,
 * and a mail already sent cannot be re-pointed.
 */
const invoiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/invoices/$id',
  component: InvoiceScreen,
})

/*
 * Public, and NOT indexable — the opposite of the legal pages. The link lands
 * here from a mailbox that may be on a device with no session at all, so it
 * cannot sit behind the app's authenticated tree; and a URL that carries a
 * single-use capability in its query string must never reach a crawler.
 */
const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/confirm-email',
  component: ConfirmEmailScreen,
  validateSearch: confirmEmailSearchSchema,
})

/*
 * Forgotten password, both halves. Public for the obvious reason: somebody who
 * cannot sign in cannot be asked to. Not indexable — the second carries a
 * single-use capability in its query string.
 */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/forgot-password',
  component: RequestPasswordResetScreen,
})

/*
 * Where the mobile app's browser lands, carrying a one-time code in the
 * fragment. Under `/account` so it renders standalone — no app shell around a
 * page whose visitor is not signed in yet.
 */
const sessionHandoffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/handoff',
  component: SessionHandoffScreen,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/reset-password',
  component: ConfirmPasswordResetScreen,
  validateSearch: resetPasswordSearchSchema,
})

/*
 * Where an organisation seat invitation lands. The path is the backend's — its
 * mail links here — and the screen is the password form under invitation copy.
 *
 * Public, like the other two `/account/…` links: the person arriving has an
 * account in `PENDING` and no password, so requiring a session would refuse
 * exactly the people it exists for.
 */
const seatInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/team/invitation',
  component: AcceptSeatInvitationScreen,
  validateSearch: seatInvitationSearchSchema,
})

const seatsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/seats',
  component: SeatsScreen,
  beforeLoad: requireSurface('seats'),
})

const routeTree = rootRoute.addChildren([
  homeRoute,
  signupRoute,
  loginRoute,
  privacyRoute,
  termsRoute,
  supportRoute,
  journeyRoute,
  cohortRoute,
  resourcesRoute,
  readingRoute,
  courseRoute,
  courseModuleRoute,
  collectionRoute,
  audioRoute,
  printableRoute,
  podcastsRoute,
  podcastRoute,
  qandaRoute,
  qandaItemRoute,
  casesRoute,
  caseStudyRoute,
  eventsRoute,
  communityRoute,
  roomRoute,
  threadRoute,
  bookmarksRoute,
  challengeRoute,
  searchRoute,
  announcementsRoute,
  profileRoute,
  blockedMembersRoute,
  practitionerRoute,
  billingRoute,
  changePasswordRoute,
  changeEmailRoute,
  deleteAccountRoute,
  changePlanRoute,
  manageSubscriptionRoute,
  paymentMethodRoute,
  invoicesRoute,
  invoiceRoute,
  confirmEmailRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  seatInvitationRoute,
  sessionHandoffRoute,
  seatsRoute,
])

/**
 * `basepath` is what makes `/en/…` a real URL rather than a redirect.
 *
 * The route tree is declared once, in language-neutral paths. Mounting it under
 * `/en` means `/en/journey` resolves to the `/journey` route with no duplicate
 * definitions and no per-language route table to keep in step.
 *
 * Read from the document at construction because the prefix cannot change
 * without a full load: switching language navigates, it does not re-mount.
 */
export function makeRouter(context: RouterContext) {
  // The LITERAL prefix in the URL, not `LOCALE_PREFIX[locale]` — French has two
  // forms (canonical unprefixed and the `/fr` alias) and the router has to
  // match whichever the visitor's URL carries. Link generation still uses
  // `LOCALE_PREFIX` so we never rewrite people to `/fr/…`.
  const basepath = basepathFor(window.location.pathname)
  return createRouter({ routeTree, context, defaultPreload: 'intent', basepath })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof makeRouter>
  }
}
